import { usesHostedBilling, usesStoreBilling } from '@/lib/appDistribution';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform, Alert } from 'react-native';
import useTheme from '@/hooks/useNeutralTheme';
import { isDarkTheme } from '@/constants/Colors';
import { useCreditPacksStore, useModalStore, useAuthStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { getCreditPackIapProductId } from '@/hooks/useIosIap';
import { nativeCreditPackPrice } from '@/lib/nativePayment';
import AppStoreCatalogStatus from './AppStoreCatalogStatus';
import useResponsiveness from '@/hooks/useResponsiveness';
import CreditPackCard from '@/components/Credits/CreditPackCard';
import CreditsFAQs from '@/components/Credits/CreditsFAQs';
import PayWithBitcoinRow from '@/components/Credits/PayWithBitcoinRow';
import PremiumUpsellRow from '@/components/Credits/PremiumUpsellRow';
import AutoTopUpOfferRow from '@/components/Credits/AutoTopUpOfferRow';
import InternationalPaymentNotice from '@/components/InternationalPaymentNotice';
import BannerPlacement from '@/components/Banner/BannerPlacement';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { PLUS_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { formatCreditBalance } from '@/lib/formatCredits';
import { trackEvent } from '@/constants/utils/analytics';

function CreditsPurchaseModalStore({
  isPlus,
  isPlusPremium,
  isIapSubscriber,
  onClose,
  totalAvailableCredits,
  monthlyCredits,
  onLowerActionsChange,
  promoCode,
}) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isDark, isXS), [colors, isDark, isXS]);
  const { openModal } = useModalStore(state => state.actions);
  const { openCreditPacksCheckout, usesWhopCheckout, iap } = useGabPurchase();

  const creditPacks = useCreditPacksStore(state => state.creditPacks);
  const fetchCreditPacks = useCreditPacksStore(state => state.fetchCreditPacks);

  const hasPaymentMethod = useAuthStore(state => state.user?.valmar?.hasPaymentMethod);
  const maskedCardData = useAuthStore(state => state.user?.valmar?.maskedCardData);
  const canQuickPurchase = usesHostedBilling() && !usesWhopCheckout && hasPaymentMethod && maskedCardData?.maskedNumber;

  const [selectedPackId, setSelectedPackId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    Promise.resolve(fetchCreditPacks()).catch(() => {});
  }, [fetchCreditPacks]);

  const displayedPacks = useMemo(() => {
    if (!creditPacks) return [];
    return [...creditPacks]
      .filter(p => p.flags?.isDisplayed !== false)
      .sort((a, b) => (a.orderWeight ?? 0) - (b.orderWeight ?? 0));
  }, [creditPacks]);

  const basePerCredit = useMemo(() => {
    if (displayedPacks.length === 0) return 0;
    const sorted = [...displayedPacks].sort((a, b) => (a.credits || 0) - (b.credits || 0));
    const smallest = sorted[0];
    return smallest.credits > 0 ? smallest.price / smallest.credits : 0;
  }, [displayedPacks]);

  useEffect(() => {
    if (selectedPackId || displayedPacks.length === 0) return;
    const popular = displayedPacks.find(p => p.flags?.isPopular);
    const bestValue = displayedPacks.find(p => p.flags?.isBestValue);
    const fallback = displayedPacks.length > 1 ? displayedPacks[1] : displayedPacks[0];
    setSelectedPackId((popular || bestValue || fallback)?.id);
  }, [displayedPacks, selectedPackId]);

  const selectedPack = useMemo(
    () => displayedPacks.find(p => p.id === selectedPackId),
    [displayedPacks, selectedPackId],
  );

  const getPackPrice = useCallback((pack) => {
    if (!pack) return '';
    if (usesStoreBilling()) {
      const iapProductId = getCreditPackIapProductId(pack.credits);
      const iapProduct = iapProductId ? iap?.getProduct?.(iapProductId) : null;
      return nativeCreditPackPrice(iapProduct) || 'Store price unavailable';
    }
    return `$${(pack.price ?? 0).toFixed(2)}`;
  }, [iap]);

  const purchaseBlocked = isLoading || (usesStoreBilling() && (iap?.purchasePending || iap?.purchaseInProgress
    || !nativeCreditPackPrice(iap?.getProduct?.(getCreditPackIapProductId(selectedPack?.credits)))));

  const handleBuy = useCallback(async () => {
    if (!selectedPack || purchaseBlocked) return;
    trackEvent('conversion', 'credit_pack_selected', String(selectedPack.credits), selectedPack.price);
    setIsLoading(true);
    try {
      if (canQuickPurchase) {
        openModal('QuickPurchaseConfirmModal', { creditPackId: selectedPack.id });
      } else {
        await openCreditPacksCheckout({ creditPackId: selectedPack.id, credits: selectedPack.credits, promoCode });
      }
    } catch (error) {
      console.error('Store variant purchase error:', error);
      if (Platform.OS !== 'web') {
        Alert.alert('Purchase Error', error?.message || 'Failed to purchase credit pack.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedPack, canQuickPurchase, openModal, openCreditPacksCheckout, promoCode, purchaseBlocked]);

  // Push the buy action up to the parent modal as a lowerAction
  useEffect(() => {
    if (!onLowerActionsChange) return;
    const selectedPriceLabel = selectedPack ? getPackPrice(selectedPack) : '';
    const selectedCreditsLabel = selectedPack ? (selectedPack.credits ?? 0).toLocaleString() : '';

    const title = selectedPack
      ? (isXS ? `Buy — ${selectedPriceLabel}` : `Buy ${selectedCreditsLabel} Credits — ${selectedPriceLabel}`)
      : 'Select a credit pack';

    onLowerActionsChange([
      {
        title,
        icon: 'coins',
        variant: 'purple',
        onPress: handleBuy,
        disabled: !selectedPack || purchaseBlocked,
        isLoading,
      },
    ]);
  }, [selectedPack, isLoading, isXS, handleBuy, getPackPrice, onLowerActionsChange, purchaseBlocked]);

  const handleUpgrade = useCallback(() => {
    onClose?.();
    openModal('PricingModal');
  }, [onClose, openModal]);

  return (
    <View style={styles.container}>
      <AppStoreCatalogStatus iap={iap} credits />
      <BannerPlacement placement="credit-store" />

      <View style={styles.sectionHeader}>
        <View style={styles.titleRow}>
          <Text size="xl" weight="bold" color="primary">Choose a credit pack</Text>
          <View style={styles.balancePill}>
            <Icon name="coins" size={11} color={colors.text_color_secondary} />
            <Text size="xs" weight="semibold" color="secondary">
              {formatCreditBalance(totalAvailableCredits)} credits
            </Text>
          </View>
        </View>
        <Text size="s" color="secondary">One-time purchase — credits never expire</Text>
      </View>

      <InternationalPaymentNotice />

      <View style={styles.packsGrid}>
        {displayedPacks.map((pack) => {
          const perCredit = pack.credits > 0 ? pack.price / pack.credits : 0;
          const savingsPercent = basePerCredit > 0 && perCredit < basePerCredit
            ? Math.round((1 - perCredit / basePerCredit) * 100)
            : 0;

          return (
            <CreditPackCard
              key={pack.id}
              credits={pack.credits}
              price={pack.price}
              name={pack.name}
              isBestValue={pack.flags?.isBestValue}
              isPopular={pack.flags?.isPopular}
              isSelected={selectedPackId === pack.id}
              onSelect={() => setSelectedPackId(pack.id)}
              savingsPercent={savingsPercent}
              priceLabel={getPackPrice(pack)}
              disabled={isLoading}
            />
          );
        })}
      </View>

      <PayWithBitcoinRow
        creditPackId={selectedPack?.id}
        price={selectedPack?.price}
        disabled={isLoading}
      />

      {!isPlus && (
        <Pressable
          style={({ hovered }) => [
            styles.plusRow,
            hovered && styles.plusRowHovered,
          ]}
          onPress={handleUpgrade}
        >
          <Icon name="sparkles" size={16} color={colors.text_color_primary} solid />
          <View style={styles.plusTextContainer}>
            <Text size="s" weight="semibold" color="primary">
              Or upgrade to Plus
            </Text>
            <Text size="xs" color="secondary">
              Free messages + {PLUS_MONTHLY_CREDITS_DISPLAY} credits/mo — starting at $16.67/mo
            </Text>
          </View>
          <Icon name="chevron-right" size={12} color={colors.text_color_tertiary} />
        </Pressable>
      )}

      {/* Plus Premium is web-only; never upsell on top of App Store / Play Plus. */}
      {isPlus && !isPlusPremium && !isIapSubscriber && usesHostedBilling() && (
        <PremiumUpsellRow onClose={onClose} />
      )}

      <AutoTopUpOfferRow />

      <CreditsFAQs />

      <View style={styles.trustRow}>
        <Icon name="shield-check" size={12} color={colors.text_color_tertiary} />
        <Text size="xs" color="tertiary">Secure checkout</Text>
        <View style={styles.trustDot} />
        <Icon name="infinity" size={12} color={colors.text_color_tertiary} />
        <Text size="xs" color="tertiary">Never expires</Text>
        <View style={styles.trustDot} />
        <Icon name="bolt" size={11} color={colors.text_color_tertiary} />
        <Text size="xs" color="tertiary">Instant delivery</Text>
      </View>
    </View>
  );
}

const createStyles = (colors, isDark, isXS) => StyleSheet.create({
  container: {
    gap: isXS ? 14 : 16,
    padding: isXS ? 16 : 24,
  },
  sectionHeader: {
    gap: 2,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  balancePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: colors.solid_color_tertiary,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
  },
  packsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  plusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 0,
  },
  plusRowHovered: {
    backgroundColor: colors.solid_color_block,
  },
  plusTextContainer: {
    flex: 1,
  },
  trustRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    flexWrap: 'wrap',
    paddingVertical: 4,
  },
  trustDot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: colors.text_color_tertiary,
    opacity: 0.5,
    marginHorizontal: 2,
  },
});

export default React.memo(CreditsPurchaseModalStore);
